import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import classnames from 'classnames';
import { Card } from 'tea-component';
import Filter, { IFilterInstance } from '@/pages/intelligence/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useFilterUseHook from '@/pages/intelligence/hooks/useFilterUseHook';
import useGenreComponent from '@/pages/intelligence/hooks/useGenreComponent';
import useSenTowerTag from '@/pages/intelligence/hooks/useSenTowerTag';
import { formatFilterSaveValue } from '@intelligence/utils/filterUtils';
import { handleRangeMiniGameMoreFilterExtend } from '@intelligence/utils/dateUtils';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import * as FILTER_KEYS from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { FILTER_DEFAULT_INPUT_LIST, FILTER_DEFAULT_VALUE } from './_config';
import './index.scss';
import type { IBaseProps, IRegion } from '@/pages/intelligence/types/common';
import type { IMobileStoreRankMoreFilter, IStoreRankMoreCoreFetchParams } from '@/pages/intelligence/types/storeRank';
import WeixinIcon from '@intelligence/assets/imgs/casualDailyReports/icon-weixin.png';
import { useDeepCompareEffect } from 'ahooks';

interface IMobileStoreRankMoreFilterProps extends IBaseProps {
  isOperate: boolean;
  initialCheckAllCountries: boolean;
  regionOptions: IRegion[] | null;
  filter: IMobileStoreRankMoreFilter | IStoreRankMoreCoreFetchParams;
  onInit: () => void;
  onConfirm: (payload: IMobileStoreRankMoreFilter) => void;
  onReset: () => void;
  rangeTimeComponentDefault: any;
  buttonLogConfig?: Record<string, any>;
  entityType?: string;
}

/** MgrMoreFilter 组件实例接口 */
export interface IMgrMoreFilterInstance {
  setValueAndConfirm: (partialValue: Record<string, any>) => void;
}

const WechatGamesMoreFilter: React.ForwardRefRenderFunction<IMgrMoreFilterInstance, IMobileStoreRankMoreFilterProps> = (props, ref) => {
  const {
    style,
    className,
    regionOptions,
    filter,
    onInit,
    onConfirm,
    onReset,
    rangeTimeComponentDefault,
    entityType,
  } = props;

  const { filterExtend: filterExtendTmp, setFilterExtend, resetFilterExtend } = useFilterExtend({});
  const { tagConfig } = useSenTowerTag({});
  const initialFormValue = (() => {
    const filterExtend = handleRangeMiniGameMoreFilterExtend(filterExtendTmp);
    const genreSelectFilterExtend = filterExtend?.[FILTER_KEYS.GENRETOR];
    const rangeTimeDefault = [filter?.start_date || '', filter?.end_date || ''];
    const genreSelectDefault = FILTER_DEFAULT_VALUE[FILTER_KEYS.GENRETOR];
    const myFilterFilterExtend = filterExtend?.[FILTER_KEYS.MY_FILTER]?.value;
    return {
      [FILTER_KEYS.MY_FILTER]: {
        value: myFilterFilterExtend ?? '',
      },
      [FILTER_KEYS.RANGE_TIME]: {
        type: 'date',
        value: rangeTimeDefault,
      },
      [FILTER_KEYS.GENRETOR]: {
        source: 'wechat_minigame',
        mainGenres: genreSelectFilterExtend?.genretor?.mainGenres ?? genreSelectDefault?.mainGenres,
        subGenres: genreSelectFilterExtend?.genretor?.subGenres ?? genreSelectDefault?.subGenres,
        minigame_genre: genreSelectFilterExtend?.genretor?.minigame_genre ?? genreSelectDefault?.minigame_genre,
      },
    };
  })();

  const {
    genreRef,
    genreSource,
    mainGenres,
    subGenres,
    level4Genres,
    handleGenresChange,
    handleReset: handleGenresReset,
  } = useGenreComponent(props, undefined, { defaultGenreSource: GENRE_SOURCE_MAP.MINIGAME });

  const [inputList, setInputList] = useState(FILTER_DEFAULT_INPUT_LIST);
  const [formValue, setFormValue] = useState(initialFormValue);

  useDeepCompareEffect(() => {
    setFormValue(initialFormValue);
  }, [initialFormValue]);

  const formatFormValue = (values: any) => {
    const newFilterValues = formatFilterSaveValue({
      values,
      timeConfig: {
        // 天粒度单选1天
        needSameDay: true,
      },
    });
    return newFilterValues;
  };

  const { onSaveFilterMsg, onFilterChange } = useFilterUseHook({
    defaultValue: FILTER_DEFAULT_VALUE,
    formValue,
    setFormValue,
    timeFilterValues: {
      type: 'daily',
      value: [filter?.start_date, filter?.end_date]
    },
    formatFormValue,
  });

  const handleInit = () => {
    onInit?.();
  };

  const handleConfirm = (value: any, apiValue: any) => {
    // console.debug('handleConfirm1111', value)
    // console.debug('handleConfirm1111apiValue', apiValue)
    setFormValue(value);

    const filterForm = {
      start_date: apiValue?.[FILTER_KEYS.RANGE_TIME]?.start_date,
      end_date: apiValue?.[FILTER_KEYS.RANGE_TIME]?.end_date,
      minigame_genre: apiValue[FILTER_KEYS.GENRETOR]?.minigame_genre || [],
      [FILTER_KEYS.SENSOR_TOWER_TAG_PRODUCT_MODEL]: apiValue[FILTER_KEYS.SENSOR_TOWER_TAG_PRODUCT_MODEL],
    };

    const apiValueTmp = {
      ...apiValue,
    };
    onConfirm?.(filterForm);
    // 点击确认后，将筛选器保存方案及保存数据存到缓存中
    onSaveFilterMsg(value, apiValue);
    setFilterExtend(apiValueTmp);
  };

  const handleReset = () => {
    const newFormValue = {
      ...FILTER_DEFAULT_VALUE,
      [FILTER_KEYS.RANGE_TIME]: {
        type: 'date',
        value: rangeTimeComponentDefault?.date,
      },
    };
    setFormValue(newFormValue);
    genreRef.current?.reset();
    resetFilterExtend();
    onReset?.();
  };

  const updateTimeComponentDefault = (inputListParam: any[]) => {
    const inputListTmp = inputListParam?.map((item) => {
      // 更新最早有数据时间
      if (rangeTimeComponentDefault) {
        if (item?.key === FILTER_KEYS.RANGE_TIME) {
          return {
            ...item,
            componentDefault: rangeTimeComponentDefault,
          };
        }
      }
      return item;
    });
    return inputListTmp;
  };

  useEffect(() => {
    let inputListTmp = [...inputList];

    // 配置 Genre 筛选器 (四级品类)
    const genreSelectTmp = inputListTmp.find((item) => item?.key === FILTER_KEYS.GENRETOR);
    if (genreSelectTmp) {
      // @ts-ignore
      genreSelectTmp.genreData = {
        genreRef,
        genreSource,
        mainGenres,
        subGenres,
        handleGenresChange,
        handleGenresReset,
      };
      genreSelectTmp.sources = ['wechat_minigame'];
      genreSelectTmp.overlayClassName = 'weixin-mode';
      // @ts-ignore
      genreSelectTmp.entityType = entityType;
      genreSelectTmp.tipTextOutside = {
        remarks: (
          <div>
            Data of Genre comes from the <img className="dbi-genre-select-tip-text-remark-img" src={WeixinIcon} alt="" />
            WeChat Mini Game leaderboard on the Gravity Engine.
          </div>
        ),
        remarksCN: (
          <div>
            品类数据来源于第三方平台引力引擎的<img className="dbi-genre-select-tip-text-remark-img" src={WeixinIcon} alt="" />
            微信小游戏榜单。
          </div>
        ),
      };
    }
    inputListTmp = updateTimeComponentDefault(inputListTmp);
    setInputList(inputListTmp);
  }, [
    regionOptions,
    genreRef,
    genreSource,
    mainGenres,
    subGenres,
    level4Genres,
    handleGenresChange,
    handleGenresReset,
    rangeTimeComponentDefault,
    tagConfig,
  ]);

  const filterRef = useRef<IFilterInstance | null>(null);

  // 暴露 setValueAndConfirm 方法给父组件
  useImperativeHandle(ref, () => ({
    setValueAndConfirm: (partialValue: Record<string, any>) => {
      filterRef.current?.setValueAndConfirm(partialValue);
    },
  }));

  return (
    <Card style={style} className={classnames('dbi-mgrmorefilter', className)}>
      <Card.Body>
        <Filter
          ref={filterRef}
          showButton={true}
          inputList={inputList}
          defaultValue={{
            ...FILTER_DEFAULT_VALUE,
            [FILTER_KEYS.RANGE_TIME]: {
              type: 'date',
              value: rangeTimeComponentDefault?.date
            },
          }}
          value={formValue}
          onInit={handleInit}
          onConfirm={handleConfirm}
          onReset={handleReset}
          onChange={onFilterChange}
          isHaveFilterSave={true}
        />
      </Card.Body>
    </Card>
  );
};

export default forwardRef(WechatGamesMoreFilter);
